/**
 * `/stores` — the Shopify store registry (admin). List the connected stores, register a new store's
 * config, edit its brand label / API version, and activate/deactivate it (deactivating pauses the
 * 15-min reconcile poll for that store).
 *
 * Phase 1b of the store-connect plan: this manages the NON-secret config only. Registering a store
 * here writes its config row — an admin must still add the store's Shopify credentials before orders
 * flow; the guided OAuth connect that automates that lands in Phase 2. Writes are admin-gated in the
 * UI (cosmetic — the server 403 is the real gate, see lib/roles).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Power,
  PowerOff,
  RefreshCw,
  Store,
  Unplug,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiClient } from '@/lib/api/client';
import { useApiClient } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth-state';
import {
  authTypeLabel,
  type InstallResult,
  isValidApiVersion,
  parseInstallResult,
  type StoreDraft,
  type StoreSummary,
  sortStores,
  validateConnectDomain,
  validateStoreRegister,
} from './stores-lib';

// ── Data ──

function useStores() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const { data, error } = await client.GET('/stores');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load stores.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return sortStores(data.data.items as StoreSummary[]);
    },
  });
}

export function StoresPage() {
  const query = useStores();
  const { state: authState } = useAuth();
  const isAdmin = authState.status === 'signed-in' && authState.role.isAdmin;
  const stores = query.data ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Stores</h1>
          <p className="text-sm text-muted-foreground">
            The Shopify storefronts connected to Fast Screen Print. “Connect a store” walks through
            Shopify’s approval to authorize the app. Deactivating a store pauses its order polling;
            disconnecting also revokes its access.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={query.isFetching ? 'size-4 animate-spin' : 'size-4'} />
            Refresh
          </Button>
          <ConnectStore isAdmin={isAdmin} />
        </div>
      </header>

      <InstallResultBanner onResolved={() => query.refetch()} />

      {query.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load stores</AlertTitle>
          <AlertDescription>
            {query.error instanceof Error ? query.error.message : 'Network error.'}
          </AlertDescription>
        </Alert>
      )}

      {query.isPending && <Skeleton className="h-40 w-full" />}

      {query.isSuccess &&
        (stores.length === 0 ? <EmptyState /> : <StoreTable stores={stores} isAdmin={isAdmin} />)}
    </div>
  );
}

/** Header actions: the primary OAuth "Connect a store" + the secondary manual (client_credentials) register. */
function ConnectStore({ isAdmin }: { isAdmin: boolean }) {
  const [connectOpen, setConnectOpen] = useState(false);
  return (
    <>
      <StoreDialog mode="create" isAdmin={isAdmin} />
      <Button
        size="sm"
        disabled={!isAdmin}
        title={isAdmin ? undefined : 'Only an admin can connect stores'}
        onClick={() => setConnectOpen(true)}
      >
        <Link2 className="size-4" />
        Connect a store
      </Button>
      <ConnectDialog open={connectOpen} onClose={() => setConnectOpen(false)} />
    </>
  );
}

/** The OAuth connect flow: collect the shop domain, then hand off to Shopify's consent screen. */
function ConnectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const client = useApiClient();
  const [shopDomain, setShopDomain] = useState('');
  const [brandLabel, setBrandLabel] = useState('');

  useEffect(() => {
    if (open) {
      setShopDomain('');
      setBrandLabel('');
    }
  }, [open]);

  const valid = validateConnectDomain(shopDomain);
  const mutation = useMutation({
    mutationFn: async () => {
      const res = await client.POST('/stores/oauth/begin', {
        body: {
          shopDomain: shopDomain.trim().toLowerCase(),
          brandLabel: brandLabel.trim() || undefined,
        },
      });
      if (res.error || !res.data?.ok) {
        throw new Error(res.error?.error?.detail ?? 'Could not start the connection.');
      }
      return res.data.data.authorizeUrl;
    },
    onSuccess: (authorizeUrl) => {
      // Leave the SPA for Shopify's consent screen; the public callback redirects back to /stores.
      window.location.href = authorizeUrl;
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          mutation.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect a store</DialogTitle>
          <DialogDescription>
            Enter the store’s Shopify domain. You’ll be sent to Shopify to approve read access to
            its orders and products, then returned here. You must be able to approve the app on that
            store.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="connect-domain">Shopify domain</Label>
            <Input
              id="connect-domain"
              value={shopDomain}
              onChange={(e) => setShopDomain(e.target.value)}
              placeholder="brand.myshopify.com"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              The store’s <code>.myshopify.com</code> domain. FSP requests read-only access (orders
              + products).
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="connect-brand">Brand label (optional)</Label>
            <Input
              id="connect-brand"
              value={brandLabel}
              onChange={(e) => setBrandLabel(e.target.value)}
              placeholder="Low Trendz"
            />
          </div>
          {mutation.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : 'Could not start the connection.'}
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!valid || mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Link2 className="size-4" />
            )}
            Continue to Shopify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function installErrorMessage(code: string): string {
  switch (code) {
    case 'scope_insufficient':
      return 'Shopify didn’t grant the required read access (orders + products). Re-approve with the requested permissions.';
    case 'connect_failed':
      return 'We couldn’t complete the token exchange with Shopify. Please try connecting again.';
    default:
      return 'Something went wrong finishing the connection. Please try again.';
  }
}

/** One-shot banner reporting the OAuth callback outcome (from the ?connected/?install= redirect flags). */
function InstallResultBanner({ onResolved }: { onResolved: () => void }) {
  const [result, setResult] = useState<InstallResult | null>(null);

  useEffect(() => {
    const parsed = parseInstallResult(window.location.search);
    if (!parsed) return;
    setResult(parsed);
    // Clear the flags (keep the path) so a refresh doesn't re-show them.
    window.history.replaceState(null, '', window.location.pathname);
    if (parsed.kind === 'connected') onResolved();
  }, [onResolved]);

  if (!result) return null;
  if (result.kind === 'connected') {
    return (
      <Alert className="border-emerald-300">
        <CheckCircle2 className="size-4 text-emerald-600" />
        <AlertTitle>Store connected</AlertTitle>
        <AlertDescription>
          “{result.storeId}” is connected. Its catalog is syncing and new orders will flow in.
        </AlertDescription>
      </Alert>
    );
  }
  if (result.kind === 'cancelled') {
    return (
      <Alert>
        <XCircle className="size-4" />
        <AlertTitle>Install cancelled</AlertTitle>
        <AlertDescription>
          The connection was cancelled before it finished. You can start again any time.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert variant="destructive">
      <XCircle className="size-4" />
      <AlertTitle>Connection failed</AlertTitle>
      <AlertDescription>{installErrorMessage(result.code)}</AlertDescription>
    </Alert>
  );
}

function StoreTable({ stores, isAdmin }: { stores: StoreSummary[]; isAdmin: boolean }) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Store</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Auth</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Status
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground sr-only">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {stores.map((store) => (
            <tr key={store.storeId} className="border-t [&:nth-child(even)]:bg-muted/20">
              <td className="px-3 py-2 align-top">
                <div className="flex items-center gap-2">
                  <Store className="size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="font-medium">{store.brandLabel ?? store.storeId}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {store.shopDomain} · {store.storeId}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-3 py-2 align-top">
                <Badge variant="secondary">{authTypeLabel(store.authType)}</Badge>
              </td>
              <td className="px-3 py-2 align-top">
                {store.active ? (
                  <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                    Active
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Paused
                  </Badge>
                )}
              </td>
              <td className="px-3 py-2 text-right align-top">
                <StoreRowActions store={store} isAdmin={isAdmin} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StoreRowActions({ store, isAdmin }: { store: StoreSummary; isAdmin: boolean }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<null | 'edit' | 'deactivate' | 'disconnect'>(null);
  const adminTitle = isAdmin ? undefined : 'Only an admin can manage stores';

  const activate = useMutation({
    mutationFn: async () => {
      const res = await client.PATCH('/stores/{storeId}', {
        params: { path: { storeId: store.storeId } },
        body: { active: true },
      });
      if (res.error || !res.data?.ok) throw new Error(res.error?.error?.detail ?? 'Failed.');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stores'] }),
  });

  return (
    <div className="flex items-center justify-end gap-1">
      {store.active ? (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive"
          disabled={!isAdmin}
          title={adminTitle}
          onClick={() => setDialog('deactivate')}
        >
          <PowerOff className="size-4" /> Deactivate
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          disabled={!isAdmin || activate.isPending}
          title={adminTitle}
          onClick={() => activate.mutate()}
        >
          {activate.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Power className="size-4" />
          )}
          Activate
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        disabled={!isAdmin}
        title={adminTitle}
        onClick={() => setDialog('edit')}
        aria-label="Edit store"
      >
        <Pencil className="size-4" />
      </Button>
      {store.authType === 'oauth' && (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive"
          disabled={!isAdmin}
          title={adminTitle}
          onClick={() => setDialog('disconnect')}
        >
          <Unplug className="size-4" /> Disconnect
        </Button>
      )}
      <StoreDialog
        mode="edit"
        store={store}
        isAdmin={isAdmin}
        open={dialog === 'edit'}
        onClose={() => setDialog(null)}
      />
      <DeactivateDialog
        store={store}
        open={dialog === 'deactivate'}
        onClose={() => setDialog(null)}
      />
      <DisconnectDialog
        store={store}
        open={dialog === 'disconnect'}
        onClose={() => setDialog(null)}
      />
    </div>
  );
}

async function saveStore(
  client: ApiClient,
  mode: 'create' | 'edit',
  draft: StoreDraft,
  storeId?: string,
): Promise<void> {
  if (mode === 'create') {
    const res = await client.POST('/stores', {
      body: {
        storeId: draft.storeId.trim(),
        shopDomain: draft.shopDomain.trim(),
        brandLabel: draft.brandLabel.trim() || undefined,
        apiVersion: draft.apiVersion.trim() || undefined,
      },
    });
    if (res.error || !res.data?.ok) {
      throw new Error(res.error?.error?.detail ?? 'Could not register the store.');
    }
    return;
  }
  // Edit — shopDomain/storeId are immutable; send the editable fields. Always include brandLabel
  // (empty clears it back to the storeId slug) so PATCH always has ≥1 field.
  const body: { brandLabel: string; apiVersion?: string } = { brandLabel: draft.brandLabel.trim() };
  if (draft.apiVersion.trim()) body.apiVersion = draft.apiVersion.trim();
  const res = await client.PATCH('/stores/{storeId}', {
    // biome-ignore lint/style/noNonNullAssertion: edit mode always passes storeId
    params: { path: { storeId: storeId! } },
    body,
  });
  if (res.error || !res.data?.ok) {
    throw new Error(res.error?.error?.detail ?? 'Could not update the store.');
  }
}

function StoreDialog({
  mode,
  store,
  isAdmin,
  open: controlledOpen,
  onClose,
}: {
  mode: 'create' | 'edit';
  store?: StoreSummary;
  isAdmin: boolean;
  open?: boolean;
  onClose?: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [selfOpen, setSelfOpen] = useState(false);
  const open = mode === 'create' ? selfOpen : (controlledOpen ?? false);
  const close = () => (mode === 'create' ? setSelfOpen(false) : onClose?.());

  const [storeId, setStoreId] = useState(store?.storeId ?? '');
  const [shopDomain, setShopDomain] = useState(store?.shopDomain ?? '');
  const [brandLabel, setBrandLabel] = useState(store?.brandLabel ?? '');
  const [apiVersion, setApiVersion] = useState(store?.apiVersion ?? '');

  useEffect(() => {
    if (open) {
      setStoreId(store?.storeId ?? '');
      setShopDomain(store?.shopDomain ?? '');
      setBrandLabel(store?.brandLabel ?? '');
      setApiVersion(store?.apiVersion ?? '');
    }
  }, [open, store]);

  const draft: StoreDraft = { storeId, shopDomain, brandLabel, apiVersion };
  const valid = mode === 'create' ? validateStoreRegister(draft) : isValidApiVersion(apiVersion);

  const mutation = useMutation({
    mutationFn: () => saveStore(client, mode, draft, store?.storeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stores'] });
      close();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (mode === 'create') setSelfOpen(o);
        if (!o) {
          mutation.reset();
          onClose?.();
        }
      }}
    >
      {mode === 'create' && (
        <Button
          size="sm"
          variant="outline"
          disabled={!isAdmin}
          title={isAdmin ? undefined : 'Only an admin can register stores'}
          onClick={() => setSelfOpen(true)}
        >
          <Plus className="size-4" />
          Register manually
        </Button>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Register a store' : 'Edit store'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Advanced: registers an org-owned store’s non-secret config for the client-credentials grant (its token is minted from the app). For third-party brands use “Connect a store” instead.'
              : 'The store slug and Shopify domain are fixed. Update the brand label or pinned API version.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="store-id">Store ID (slug)</Label>
            <Input
              id="store-id"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              placeholder="low-trendz"
              disabled={mode === 'edit'}
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, numbers, hyphens. Our stable internal key — cannot change later.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="store-domain">Shopify domain</Label>
            <Input
              id="store-domain"
              value={shopDomain}
              onChange={(e) => setShopDomain(e.target.value)}
              placeholder="brand.myshopify.com"
              disabled={mode === 'edit'}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="store-brand">Brand label</Label>
              <Input
                id="store-brand"
                value={brandLabel}
                onChange={(e) => setBrandLabel(e.target.value)}
                placeholder="Low Trendz"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="store-apiv">API version</Label>
              <Input
                id="store-apiv"
                value={apiVersion}
                onChange={(e) => setApiVersion(e.target.value)}
                placeholder="2026-07"
              />
            </div>
          </div>
          {mutation.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                {mutation.error instanceof Error ? mutation.error.message : 'Save failed.'}
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => mutation.mutate()} disabled={!valid || mutation.isPending}>
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {mode === 'create' ? 'Register store' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeactivateDialog({
  store,
  open,
  onClose,
}: {
  store: StoreSummary;
  open: boolean;
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      const res = await client.PATCH('/stores/{storeId}', {
        params: { path: { storeId: store.storeId } },
        body: { active: false },
      });
      if (res.error || !res.data?.ok) {
        throw new Error(res.error?.error?.detail ?? 'Could not deactivate the store.');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stores'] });
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          mutation.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Deactivate “{store.brandLabel ?? store.storeId}”?</DialogTitle>
          <DialogDescription>
            Pauses the 15-minute reconcile poll for this store. In-flight orders and history are
            kept; reactivate any time. (Inbound webhooks are unaffected.)
          </DialogDescription>
        </DialogHeader>
        {mutation.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {mutation.error instanceof Error ? mutation.error.message : 'Deactivate failed.'}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <PowerOff className="size-4" />
            )}
            Deactivate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisconnectDialog({
  store,
  open,
  onClose,
}: {
  store: StoreSummary;
  open: boolean;
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      const res = await client.POST('/stores/{storeId}/disconnect', {
        params: { path: { storeId: store.storeId } },
      });
      if (res.error || !res.data?.ok) {
        throw new Error(res.error?.error?.detail ?? 'Could not disconnect the store.');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stores'] });
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          mutation.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Disconnect “{store.brandLabel ?? store.storeId}”?</DialogTitle>
          <DialogDescription>
            Revokes this store’s access token and pauses it. Order + catalog history is kept. To
            resume, you’ll reconnect through Shopify’s approval again.
          </DialogDescription>
        </DialogHeader>
        {mutation.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {mutation.error instanceof Error ? mutation.error.message : 'Disconnect failed.'}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Unplug className="size-4" />
            )}
            Disconnect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center">
      <Store className="size-8 text-muted-foreground" />
      <p className="text-sm font-medium">No stores connected</p>
      <p className="max-w-sm text-sm text-muted-foreground">
        Register a Shopify store to start ingesting its orders. An admin adds the store’s
        credentials after registering (guided connect is coming).
      </p>
    </div>
  );
}
